<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { DocumentType } from '../../../../Types/document'
import ModalDialog from '../UI/Modal/ModalDialog.vue'
import SimpleGroupSelect from '../UI/Form/SimpleGroupSelect.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import useModal from '../../../../Composables/useModal.ts'
import { useContractDocumentTypes } from './useContractDocumentTypes.ts'
import { computed, onMounted, ref } from 'vue'
import useLoading from '../../../../Composables/useLoading.ts'
import PlaceholderContent from '../UI/Loading/PlaceholderContent.vue'

const props = defineProps<{
  contractId: number
  documentSignId: number
  documentTypes?: Array<DocumentType>
}>()

defineExpose({
  fetchDocumentTypes,
})

const emit = defineEmits<{
  documentGenerated: []
}>()

const { getAvailableDocumentType, generateDocumentAndAddToSign } = useContractDocumentTypes()

const { loading: submitting, setLoading: setSubmitting } = useLoading(false)
const { loading, setLoading } = useLoading(false)

const { t } = useI18n()

const { modal, modalId, open, close } = useModal({ prefix: 'generate-and-add-to-sign-' })

const documentTypes = ref<Array<DocumentType>>(props.documentTypes ?? [])

const hasDocumentTypes = computed(() => documentTypes.value.length > 0)

const selectedDocumentType = ref<DocumentType | null>(null)
function setDocumentGenerateType(documentType: DocumentType): void {
  selectedDocumentType.value = documentType
}

async function fetchDocumentTypes(): Promise<void> {
  setLoading(true)
  documentTypes.value = await getAvailableDocumentType(props.contractId)
  setLoading(false)
}

async function generateDocument(): Promise<void> {
  if (!selectedDocumentType.value) {
    return
  }

  setSubmitting(true)
  try {
    await generateDocumentAndAddToSign(props.documentSignId, Number(selectedDocumentType.value))
    emit('documentGenerated')
    close()
    await fetchDocumentTypes()
  } finally {
    setSubmitting(false)
  }
}

onMounted(async () => {
  await fetchDocumentTypes()
})
</script>

<template>
  <button
    type="button"
    @click="open"
    class="btn btn-sm btn-square btn-outline btn-round btn-primary mx-1"
    style="width: max-content; height: max-content"
    e2e-button="generateAndAddToSignButton"
  >
    <i class="fa fa-lg fa-cog"></i> {{ t('documentEntity.buttonGenerate') }}
  </button>
  <ModalDialog
    ref="modal"
    :id="modalId"
    :title="t('contract.document.documentSign.titleGenerate')"
    class-modal-dialog="modal-lg"
    e2e-modal="modal-generate-and-add-to-sign"
  >
    <template #body>
      <template v-if="!loading">
        <SimpleGroupSelect
          v-if="hasDocumentTypes"
          reference="updateDocument"
          :config-options="documentTypes"
          @transmitted="setDocumentGenerateType"
          id="documentType"
        />
        <p v-else class="mt-2 text-center">{{ t('documentEntity.anyDocumentTypeAvailable') }}</p>
      </template>
      <PlaceholderContent v-else :rows="1" />
    </template>
    <template #footer>
      <ButtonAll
        margin="no"
        data-dismiss="modal"
        type="information"
        label="button.cancel"
        @click="close"
        name="close"
      />
      <ButtonAll
        margin="no"
        :type="hasDocumentTypes ? 'validation' : 'disabled'"
        icon="generate"
        label="documentEntity.buttonGeneratePopin"
        @click="generateDocument"
        :loading="submitting"
        name="generate"
      />
    </template>
  </ModalDialog>
</template>
